import { Button } from '@affine/component';
import {
  AuthContainer,
  AuthContent,
  AuthFooter,
  AuthHeader,
  AuthInput,
} from '@affine/component/auth-components';
import {
  AuthService,
  CaptchaService,
  type Server,
} from '@affine/core/modules/cloud';
import type { AuthSessionStatus } from '@affine/core/modules/cloud/entities/session';
import { invitationReturnPath } from '@affine/core/modules/cloud/invitation';
import {
  MAGICNOTES_SERVER_NAME,
  MAGICNOTES_SERVER_URL,
} from '@affine/core/modules/cloud/magicnotes';
import { UrlService } from '@affine/core/modules/url';
import { UserFriendlyError } from '@affine/error';
import { useI18n } from '@affine/i18n';
import { FrameworkScope, useLiveData, useService } from '@toeverything/infra';
import type { MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { Captcha } from './captcha';
import { ForgotPassword } from './forgot-password';
import { setGuestMode } from './guest-mode';
import { InvitationSetup } from './invitation-setup';
import * as styles from './style.css';

const FORK_REPOSITORY_URL = 'https://github.com/kanihal/AFFiNE';
const ORIGINAL_REPOSITORY_URL = 'https://github.com/toeverything/AFFiNE';

export const MagicNotesSignIn = ({
  server,
  initialEmail,
  initialSetup,
  initialForgotPassword,
  requirePasswordSignIn,
  initialToken,
  redirectUrl,
  connectionError,
  onRetry,
  onSkip,
  onAuthenticated,
}: {
  server?: Server;
  initialEmail?: string;
  initialSetup?: boolean;
  initialForgotPassword?: boolean;
  requirePasswordSignIn?: boolean;
  initialToken?: string;
  redirectUrl?: string;
  connectionError: boolean;
  onRetry: () => void;
  onSkip: () => void;
  onAuthenticated?: (status: AuthSessionStatus) => void;
}) => {
  const t = useI18n();
  const urlService = useService(UrlService);
  const auth = server?.scope.get(AuthService);
  const captcha = server?.scope.get(CaptchaService);
  const status = useLiveData(auth?.session.status$);
  const needCaptcha = useLiveData(captcha?.needCaptcha$);
  const verifyToken = useLiveData(captcha?.verifyToken$);
  const challenge = useLiveData(captcha?.challenge$);
  const [email, setEmail] = useState(initialEmail ?? '');
  const [password, setPassword] = useState('');
  const [passwordSignInCompleted, setPasswordSignInCompleted] = useState(false);
  const [setup, setSetup] = useState(
    !!initialSetup &&
      !initialForgotPassword &&
      !BUILD_CONFIG.isNative &&
      !BUILD_CONFIG.isElectron
  );
  const [recovering, setRecovering] = useState(
    !!initialForgotPassword &&
      !BUILD_CONFIG.isNative &&
      !BUILD_CONFIG.isElectron
  );
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const submitting = useRef(false);

  const openRepository = (event: MouseEvent<HTMLAnchorElement>) => {
    if (BUILD_CONFIG.isNative || BUILD_CONFIG.isElectron) {
      event.preventDefault();
      urlService.openExternal(event.currentTarget.href);
    }
  };

  useEffect(() => {
    if (
      status === 'authenticated' &&
      !recovering &&
      (!requirePasswordSignIn || passwordSignInCompleted)
    ) {
      setGuestMode(false);
      onAuthenticated?.(status);
    }
  }, [
    onAuthenticated,
    passwordSignInCompleted,
    recovering,
    requirePasswordSignIn,
    status,
  ]);

  useEffect(() => {
    // Restore saved native tokens even when this server was added on an earlier launch.
    auth?.session.revalidateOnce().catch(() => undefined);
  }, [auth]);

  const signIn = async () => {
    if (!auth || submitting.current || (needCaptcha && !verifyToken)) return;
    submitting.current = true;
    setLoading(true);
    setError(undefined);
    try {
      await auth.signInPassword({
        email: email.trim(),
        password,
        verifyToken,
        challenge,
      });
      if (requirePasswordSignIn) setPasswordSignInCompleted(true);
    } catch (err) {
      const friendlyError = UserFriendlyError.fromAny(err);
      setError(
        friendlyError.is('WRONG_SIGN_IN_CREDENTIALS') ||
          friendlyError.is('PASSWORD_REQUIRED')
          ? t['com.affine.auth.password.error']()
          : friendlyError.is('REQUEST_ABORTED')
            ? t['error.NETWORK_ERROR']()
            : t[`error.${friendlyError.name}`](friendlyError.data)
      );
      captcha?.revalidate();
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <AuthContainer>
      <AuthHeader
        title={
          recovering
            ? 'Reset your password'
            : setup
              ? 'Set up your account'
              : 'Sign in'
        }
      />
      <AuthContent>
        {recovering ? (
          <ForgotPassword
            server={server}
            email={email}
            onEmailChange={setEmail}
            redirectUrl={redirectUrl}
          />
        ) : setup ? (
          <InvitationSetup
            server={server}
            email={email}
            onEmailChange={setEmail}
            initialToken={initialToken}
            redirectUrl={redirectUrl}
          />
        ) : (
          <form
            className={styles.credentialsForm}
            onSubmit={event => {
              event.preventDefault();
              signIn().catch(console.error);
            }}
          >
            <AuthInput
              label="Server"
              aria-label="Server"
              value={MAGICNOTES_SERVER_NAME}
              readOnly
              disabled
              tabIndex={-1}
            />
            <AuthInput
              label={t['com.affine.settings.email']()}
              aria-label={t['com.affine.settings.email']()}
              placeholder={t['com.affine.auth.sign.email.placeholder']()}
              type="email"
              name="username"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              value={email}
              onChange={setEmail}
            />
            <AuthInput
              label={t['com.affine.auth.password']()}
              aria-label={t['com.affine.auth.password']()}
              placeholder="Enter your password"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={setPassword}
            />
            <button
              type="button"
              className={styles.linkButton}
              style={{
                alignSelf: 'flex-end',
                minHeight: 44,
                cursor: 'pointer',
              }}
              onClick={event => {
                event.preventDefault();
                if (BUILD_CONFIG.isNative || BUILD_CONFIG.isElectron) {
                  const url = new URL('/sign-in', MAGICNOTES_SERVER_URL);
                  url.searchParams.set('forgotPassword', 'true');
                  if (email.trim()) url.searchParams.set('email', email.trim());
                  if (redirectUrl)
                    url.searchParams.set(
                      'redirect_uri',
                      invitationReturnPath(redirectUrl, MAGICNOTES_SERVER_URL)
                    );
                  urlService.openExternal(url.toString());
                } else {
                  setPassword('');
                  setError(undefined);
                  setRecovering(true);
                }
              }}
            >
              Forgot password?
            </button>
            {server && needCaptcha && (
              <FrameworkScope scope={server.scope}>
                <Captcha />
              </FrameworkScope>
            )}
            {error && (
              <div role="alert" className={styles.signInError}>
                {error}
              </div>
            )}
            {connectionError && (
              <div role="alert" className={styles.signInError}>
                Unable to connect to magicnotes.top.{' '}
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={onRetry}
                >
                  Try again
                </button>
              </div>
            )}
            <Button
              variant="primary"
              size="extraLarge"
              block
              loading={loading}
              disabled={
                !auth ||
                loading ||
                !email.trim() ||
                !password ||
                (!!needCaptcha && !verifyToken)
              }
              data-testid="sign-in-button"
            >
              Sign in
            </Button>
          </form>
        )}
        {(setup || recovering) && connectionError && (
          <div role="alert" className={styles.signInError}>
            Unable to connect to magicnotes.top.{' '}
            <button
              type="button"
              className={styles.linkButton}
              onClick={onRetry}
            >
              Try again
            </button>
          </div>
        )}
      </AuthContent>
      <AuthFooter>
        <Button
          variant="plain"
          block
          size="large"
          onClick={() => {
            if (BUILD_CONFIG.isNative || BUILD_CONFIG.isElectron) {
              const url = new URL('/sign-in', MAGICNOTES_SERVER_URL);
              url.searchParams.set('setup', 'true');
              if (email.trim()) url.searchParams.set('email', email.trim());
              if (redirectUrl)
                url.searchParams.set(
                  'redirect_uri',
                  invitationReturnPath(redirectUrl, MAGICNOTES_SERVER_URL)
                );
              urlService.openExternal(url.toString());
            } else {
              setPassword('');
              setError(undefined);
              if (recovering) setRecovering(false);
              else setSetup(value => !value);
            }
          }}
        >
          {setup || recovering
            ? 'Back to sign in'
            : 'Invited? Set up your account'}
        </Button>
        <Button
          variant="plain"
          block
          size="large"
          className={styles.guestButton}
          onClick={() => {
            setGuestMode(true);
            onSkip();
          }}
        >
          Continue without an account
        </Button>
        <div className={styles.forkAttribution}>
          <p>This is a forked version of AFFiNE.</p>
          <div className={styles.repositoryLinks}>
            <a
              href={FORK_REPOSITORY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={openRepository}
            >
              Forked repository
            </a>
            <span aria-hidden="true">·</span>
            <a
              href={ORIGINAL_REPOSITORY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={openRepository}
            >
              Original repository
            </a>
          </div>
        </div>
      </AuthFooter>
    </AuthContainer>
  );
};
